import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { ErrorComponent } from "../../../components/error/error.component";
import { PaginatedResult } from "../../../models/default/paginated-result";
import { Analysis } from "../../../models/general/analysis";
import { Forecast, ForecastType } from "../../../models/general/forecast";
import { URLS } from "../../../services/app-urls";
import { useNavigate } from "react-router-dom";
import { BaseService } from "../../../services/base-service";
import { translate } from "../../../translate/translate";

const DISPLAYED_COLUMNS_SHOW = [
  "select",
  "description",
  "customer",
  "type",
  "creation_date",
  "created_by",
];

const DISPLAYED_COLUMNS = [
  "select",
  "description",
  "customer_name",
  "type",
  "creation_date",
  "created_by_user",
];

const SHOW_ACTIONS: string[] = [];
const DATE_COLUMNS = ["creation_date"];

const COLUMN_STYLES: Record<number, string> = {
  1: "60px",
  2: "auto",
  3: "15%",
  4: "15%",
  5: "20%",
};

const DELETE_POLL_INTERVAL_MS = 3000;
const DELETE_POLL_MAX_ATTEMPTS = 40;

const hasLinkedAnalysis = (forecast: Forecast) => {
  const value = (forecast as unknown as { has_analysis?: boolean | number | string }).has_analysis;

  return value === true || value === 1 || value === "1" || value === "true";
};

interface ForecastBulkDeleteTask {
  id: number;
  task_id: string;
}

interface ForecastBulkDeleteError {
  id: number;
  reason: string;
  dependent_analyses_count?: number;
}

interface ForecastBulkDeleteResponse {
  deleted?: number[];
  tasks?: ForecastBulkDeleteTask[];
  errors?: ForecastBulkDeleteError[];
  message?: string;
}

interface UseForecastPageReturn {
  forecast: Forecast | undefined;
  setForecast: Dispatch<SetStateAction<Forecast | undefined>>;
  dataSource: Forecast[];
  pagination: PaginatedResult<Forecast> | undefined;
  rowsPerPage: number;
  page: number;
  searchDescription: string;
  customerName: string;
  finishedGood: string;
  forecastType: ForecastType | "";
  openDeleteModal: boolean;
  selectedIds: number[];
  confirmDeleteMessage: string;

  handleChangePage: (_: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: ChangeEvent<HTMLInputElement>) => void;
  handleRowClick: (row: Forecast) => void;
  handleCloseDeleteModal: (result: unknown) => void;
  handleOpenDeleteModal: (row: Forecast) => void;
  handleSearchChange: (event: ChangeEvent<{ value: unknown }>) => void;
  handleCustomerChange: (customerName: string) => void;
  handleFinishedGoodChange: (bomNumber: string) => void;
  handleForecastTypeChange: (type: ForecastType | "") => void;
  toggleSelectOne: (id: number) => void;
  toggleSelectAll: () => void;
  search: () => void;
  isLoading: boolean;

  DISPLAYED_COLUMNS_SHOW: readonly string[];
  DISPLAYED_COLUMNS: readonly string[];
  SHOW_ACTIONS: readonly string[];
  DATE_COLUMNS: readonly string[];
  COLUMN_STYLES: Record<number, string>;
}

export const useForecastPage = (): UseForecastPageReturn => {
  const navigate = useNavigate();
  const service = new BaseService<Forecast>(URLS.FORECAST);
  const analysisService = new BaseService<Analysis>(URLS.ANALYSIS);

  const [forecast, setForecast] = useState<Forecast>();
  const [dataSource, setDataSource] = useState<Forecast[]>([]);
  const [pagination, setPagination] = useState<PaginatedResult<Forecast>>();

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [searchDescription, setSearchDescription] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [finishedGood, setFinishedGood] = useState("");
  const [forecastType, setForecastType] = useState<ForecastType | "">("");
  const [isLoading, setIsLoading] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);

  const pendingDeleteIdsRef = useRef<number[]>([]);
  const deletePollingTimeoutsRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const deleteTaskIdsRef = useRef<Record<number, string>>({});

  useEffect(() => {
    pendingDeleteIdsRef.current = pendingDeleteIds;
  }, [pendingDeleteIds]);

  const clearDeletePolling = useCallback((id: number) => {
    const timeout = deletePollingTimeoutsRef.current[id];
    if (timeout) {
      clearTimeout(timeout);
      delete deletePollingTimeoutsRef.current[id];
    }
  }, []);

  const filterPendingDeletes = useCallback((forecasts: Forecast[]) => {
    if (pendingDeleteIds.length === 0) {
      return forecasts;
    }

    const pendingIdsSet = new Set(pendingDeleteIds);
    return forecasts.filter((item) => !pendingIdsSet.has(item.id));
  }, [pendingDeleteIds]);

  const trackPendingDeletes = useCallback((ids: number[]) => {
    if (ids.length === 0) return;

    setPendingDeleteIds((current) => Array.from(new Set([...current, ...ids])));
  }, []);

  const applyOptimisticDeletion = useCallback((ids: number[]) => {
    if (ids.length === 0) return;

    const uniqueIds = Array.from(new Set(ids));
    const deletedIdsSet = new Set(uniqueIds);

    setDataSource((current) => current.filter((item) => !deletedIdsSet.has(item.id)));
    setPagination((current) => {
      if (!current) return current;

      const nextResults = current.results.filter((item) => !deletedIdsSet.has(item.id));
      const removedCount = current.results.length - nextResults.length;

      return {
        ...current,
        count:
          typeof current.count === "number"
            ? Math.max(current.count - removedCount, 0)
            : current.count,
        results: nextResults,
      };
    });

    setSelectedIds((current) => current.filter((id) => !deletedIdsSet.has(id)));
    setForecast((current) => (current && deletedIdsSet.has(current.id) ? undefined : current));
    trackPendingDeletes(uniqueIds);
  }, [trackPendingDeletes]);

  const enrichForecastsWithAnalysis = useCallback(async (forecasts: Forecast[]) => {
    if (forecasts.length === 0) {
      return forecasts;
    }

    const forecastIds = forecasts.map((item) => item.id).filter(Boolean);

    if (forecastIds.length === 0) {
      return forecasts;
    }

    try {
      analysisService.clearParameters();
      analysisService.addParameter("forecast__in", forecastIds.join(","));
      analysisService.addParameter("limit", Math.max(forecastIds.length, 100));

      const response = await analysisService.getAllPaginate();
      const analyses = Array.isArray(response.data?.results) ? response.data.results : [];
      const linkedForecastIds = new Set(
        analyses
          .map((analysis) => analysis.forecast)
          .filter((forecastId): forecastId is number => typeof forecastId === "number")
      );

      return forecasts.map((forecast) => ({
        ...forecast,
        has_analysis: hasLinkedAnalysis(forecast) || linkedForecastIds.has(forecast.id),
      }));
    } catch {
      return forecasts;
    }
  }, []);

  const fetchData = useCallback(() => {
    setIsLoading(true);
    service.clearParameters();
    if (searchDescription) service.addParameter("search", searchDescription);
    if (customerName) service.addParameter("customer_name", customerName);
    if (finishedGood) service.addParameter("finished_good", finishedGood);
    if (forecastType) service.addParameter("type", forecastType);

    service.addParameter("limit", rowsPerPage);
    service.addParameter("offset", page * rowsPerPage);

    service
      .getAllPaginate()
      .then(async (response) => {
        const forecastsWithAnalysis = await enrichForecastsWithAnalysis(response.data.results);
        const filteredResults = filterPendingDeletes(forecastsWithAnalysis);
        setPagination({
          ...response.data,
          results: filteredResults,
        });
        setDataSource(filteredResults);
      })
      .catch((error: any) => {
        ErrorComponent(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    searchDescription,
    rowsPerPage,
    page,
    customerName,
    finishedGood,
    forecastType,
    filterPendingDeletes,
    enrichForecastsWithAnalysis,
  ]);

  const search = useCallback(() => {
    if (page !== 0) {
      setPage(0);
    } else {
      fetchData();
    }
  }, [page, fetchData]);

  const finalizePendingDelete = useCallback((
    id: number,
    shouldRefresh = true,
    showSuccessToast = false
  ) => {
    clearDeletePolling(id);
    delete deleteTaskIdsRef.current[id];

    let mustRefresh = false;

    setPendingDeleteIds((current) => {
      if (!current.includes(id)) {
        return current;
      }

      const next = current.filter((itemId) => itemId !== id);
      mustRefresh = shouldRefresh && next.length === 0;
      return next;
    });

    if (mustRefresh) {
      fetchData();
    }

    if (showSuccessToast) {
      toast.success(translate.t("forecast_successfully_deleted"));
    }
  }, [clearDeletePolling, search]);

  const pollDeletedForecast = useCallback((id: number, attempt = 0) => {
    clearDeletePolling(id);

    deletePollingTimeoutsRef.current[id] = setTimeout(async () => {
      try {
        const response = await service.getById(id);

        if (response.status === 404) {
          finalizePendingDelete(id, true, true);
          return;
        }

        if (attempt + 1 >= DELETE_POLL_MAX_ATTEMPTS) {
          finalizePendingDelete(id);
          return;
        }

        pollDeletedForecast(id, attempt + 1);
      } catch {
        if (attempt + 1 >= DELETE_POLL_MAX_ATTEMPTS) {
          finalizePendingDelete(id);
          return;
        }

        pollDeletedForecast(id, attempt + 1);
      }
    }, DELETE_POLL_INTERVAL_MS);
  }, [clearDeletePolling, finalizePendingDelete, service]);

  const startDeletionMonitoring = useCallback((data?: ForecastBulkDeleteResponse) => {
    if (!data) return;

    const deletedIds = Array.isArray(data.deleted) ? data.deleted : [];
    const tasks = Array.isArray(data.tasks) ? data.tasks : [];

    if (deletedIds.length === 0) return;

    tasks.forEach((task) => {
      if (task?.id != null && task.task_id) {
        deleteTaskIdsRef.current[task.id] = task.task_id;
      }
    });

    applyOptimisticDeletion(deletedIds);
    deletedIds.forEach((id) => {
      pollDeletedForecast(id);
    });
  }, [applyOptimisticDeletion, pollDeletedForecast]);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, customerName, finishedGood, forecastType]);

  useEffect(() => {
    return () => {
      Object.values(deletePollingTimeoutsRef.current).forEach((timeout) => {
        clearTimeout(timeout);
      });
      deletePollingTimeoutsRef.current = {};
      deleteTaskIdsRef.current = {};
    };
  }, []);

  const handleChangePage = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const toggleSelectAll = useCallback(() => {
    const selectableForecasts = dataSource.filter((f) => !f.has_analysis);
    if (selectedIds.length === selectableForecasts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(selectableForecasts.map((f) => f.id));
    }
  }, [selectedIds, dataSource]);

  const toggleSelectOne = useCallback((id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  const handleBulkDeleteToast = useCallback((data?: ForecastBulkDeleteResponse) => {
    if (!data) return false;

    const deleted = Array.isArray(data.deleted) ? data.deleted : [];
    const tasks = Array.isArray(data.tasks) ? data.tasks : [];
    const errors = Array.isArray(data.errors) ? data.errors : [];

    if (data.message) {
      if (deleted.length > 0 || tasks.length > 0) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
      return true;
    }

    if (errors.length > 0) {
      const uniqueReasons = Array.from(
        new Set(errors.map((error) => error.reason).filter(Boolean))
      );

      const message = uniqueReasons.join(", ");

      if (message) {
        if (deleted.length > 0 || tasks.length > 0) {
          toast.warning(message);
        } else {
          toast.error(message);
        }
        return true;
      }
    }

    if (deleted.length > 0 || tasks.length > 0) {
      toast.info("Exclusao iniciada. Os itens sairao da lista enquanto aguardamos o processamento.");
      return true;
    }

    return false;
  }, []);

  const confirmDeleteMessage =
    selectedIds.length > 1
      ? translate.t("confirm_delete_itens")
      : translate.t("confirm_delete");

  const handleRowClick = useCallback((row: Forecast) => {
    navigate(`/general/forecast/${row.id}`);
  }, [navigate]);

  const handleOpenDeleteModal = useCallback((row: Forecast) => {
    setForecast(row);
    setOpenDeleteModal(true);
  }, []);

  const handleCloseDeleteModal = useCallback(
    async (result: unknown) => {
      if (typeof result === "boolean" && result) {
        const idsToDelete = selectedIds.length > 0 ? selectedIds : forecast ? [forecast.id] : [];

        if (idsToDelete.length === 0) {
          setOpenDeleteModal(false);
          return;
        }

        try {
          const response = await service.postFromListRoute({ ids: idsToDelete }, "bulk-delete");
          const bulkDeleteResponse = response.data as ForecastBulkDeleteResponse;

          startDeletionMonitoring(bulkDeleteResponse);
          handleBulkDeleteToast(bulkDeleteResponse);
        } catch (error: any) {
          const errorData = error?.response?.data as ForecastBulkDeleteResponse | undefined;

          startDeletionMonitoring(errorData);

          const handled = handleBulkDeleteToast(errorData);

          if (!handled) {
            ErrorComponent(error as any);
          }
        } finally {
          setSelectedIds([]);
          setForecast(undefined);
          setOpenDeleteModal(false);
        }
      } else {
        setForecast(undefined);
        setOpenDeleteModal(false);
      }
    },
    [
      selectedIds,
      forecast,
      service,
      startDeletionMonitoring,
      handleBulkDeleteToast,
    ]
  );

  const handleSearchChange = useCallback((event: ChangeEvent<{ value: unknown }>) => {
    setSearchDescription(event.target.value as string);
  }, []);

  const handleCustomerChange = useCallback((name: string) => {
    setCustomerName(name);
    setPage(0);
  }, []);

  const handleFinishedGoodChange = useCallback((bomNumber: string) => {
    setFinishedGood(bomNumber);
    setPage(0);
  }, []);

  const handleForecastTypeChange = useCallback((type: ForecastType | "") => {
    setForecastType(type);
    setPage(0);
  }, []);

  return {
    forecast,
    setForecast,
    dataSource,
    pagination,
    rowsPerPage,
    page,
    searchDescription,
    customerName,
    finishedGood,
    forecastType,
    openDeleteModal,
    selectedIds,
    confirmDeleteMessage,

    handleChangePage,
    handleChangeRowsPerPage,
    handleRowClick,
    handleCloseDeleteModal,
    handleOpenDeleteModal,
    handleSearchChange,
    handleCustomerChange,
    handleFinishedGoodChange,
    handleForecastTypeChange,
    toggleSelectOne,
    toggleSelectAll,
    search,
    isLoading,

    DISPLAYED_COLUMNS_SHOW,
    DISPLAYED_COLUMNS,
    SHOW_ACTIONS,
    DATE_COLUMNS,
    COLUMN_STYLES,
  };
};
